const formatDate = (dateString) => {
  if (!dateString) {
    return '';
  }
  return new Date(dateString).toLocaleDateString();
};

const getTrialNotice = (trial) => {
  if (!trial?.isActive || !trial?.endsAt) {
    return null;
  }
  const endsAt = new Date(trial.endsAt);
  const msLeft = endsAt.getTime() - Date.now();
  const daysLeft = Math.max(0, Math.ceil(msLeft / (1000 * 60 * 60 * 24)));
  return {
    id: 'trial',
    tone: 'info',
    label: 'Free trial',
    message: `Your trial ends on ${endsAt.toLocaleDateString()} (${daysLeft} day${daysLeft === 1 ? '' : 's'} left).`,
    ctaLabel: 'View plans',
    ctaAction: 'upgrade',
    priority: 40,
  };
};

const SUBSCRIPTION_STATUS_NOTICES = {
  past_due: {
    id: 'past-due',
    tone: 'danger',
    label: 'Payment issue',
    message: 'Your last payment failed. Update your payment method to keep access.',
    ctaLabel: 'Manage billing',
    ctaAction: 'billing',
    priority: 100,
  },
  incomplete: {
    id: 'incomplete',
    tone: 'warning',
    label: 'Setup incomplete',
    message: 'Your subscription setup is incomplete. Finish checkout to activate billing.',
    ctaLabel: 'View plans',
    ctaAction: 'upgrade',
    priority: 90,
  },
  paused: {
    id: 'paused',
    tone: 'warning',
    label: 'Subscription paused',
    message: 'Your subscription is paused. Resume billing when you are ready.',
    ctaLabel: 'Manage billing',
    ctaAction: 'billing',
    priority: 70,
  },
};

export const formatAccessStatusLabel = (activePlan) => {
  const raw = activePlan?.accessStatusLabel;
  if (!raw) {
    return null;
  }
  const map = {
    'Pilot (admin)': 'Custom plan',
    'Welcome access': 'Welcome access',
    Trial: 'Free trial',
    past_due: 'Payment due',
    incomplete: 'Setup incomplete',
    paused: 'Paused',
    active: 'Subscribed',
    trialing: 'Subscribed',
    none: null,
    Free: 'Free plan',
  };
  return map[raw] || String(raw).replace(/_/g, ' ');
};

export const getUserNotices = (activePlan) => {
  if (!activePlan) {
    return [];
  }

  const notices = [];

  if (activePlan.override?.isActive) {
    const isAdminGranted = Boolean(activePlan.override.setByAdminId);
    const planLabel = activePlan.effective?.planName || 'custom access';
    const expiresLabel = formatDate(activePlan.override.expiresAt);
    notices.push({
      id: 'pilot-override',
      tone: 'info',
      label: isAdminGranted ? 'Custom plan' : 'Welcome access',
      message: isAdminGranted
        ? `LocShark added a custom plan (${planLabel}) to your account until ${expiresLabel}. You can subscribe anytime to set up billing.`
        : `Your welcome access (${planLabel}) is active until ${expiresLabel}. Subscribe anytime to continue without interruption.`,
      ctaLabel: 'View plans',
      ctaAction: 'upgrade',
      priority: 80,
    });
  }

  const subscriptionNotice = SUBSCRIPTION_STATUS_NOTICES[activePlan.subscriptionStatus];
  if (subscriptionNotice && activePlan.hasBillingSubscription) {
    notices.push({ ...subscriptionNotice });
  }

  if (activePlan.subscription?.cancelAtPeriodEnd) {
    notices.push({
      id: 'cancel-scheduled',
      tone: 'warning',
      label: 'Cancellation scheduled',
      message: `Your subscription ends on ${formatDate(activePlan.subscription.currentPeriodEnd)}.`,
      ctaLabel: 'Manage billing',
      ctaAction: 'billing',
      priority: 60,
    });
  }

  if (activePlan.subscription?.pendingAddonChangeAt) {
    notices.push({
      id: 'pending-addon-change',
      tone: 'info',
      label: 'Add-on update scheduled',
      message: `Add-on changes take effect on ${formatDate(activePlan.subscription.pendingAddonChangeAt)}.`,
      priority: 30,
    });
  }

  const trialNotice = getTrialNotice(activePlan.trial);
  if (trialNotice && !activePlan.override?.isActive) {
    notices.push(trialNotice);
  }

  return notices
    .sort((left, right) => right.priority - left.priority)
    .filter((notice, index, all) => all.findIndex((entry) => entry.id === notice.id) === index);
};

export const getHeaderNotices = (activePlan, limit = 2) => getUserNotices(activePlan).slice(0, limit);
